/****************
 * TRANSLATIONS. 2026-09-26.
 *
 * Guillaume: CopyFi in the ten languages that matter most, web UI first
 * (mobile at the web/mobile parity pass). English is the SOURCE: every other
 * language is generated from it by scripts/i18n.mjs and never edited by hand
 * except by a reviewer (see i18n/README.md).
 *
 * Files: locales/<lang>/<namespace>.json, one namespace per area of the app,
 * so two people translating two screens never touch the same file.
 ****************/
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/** The languages we plan, in priority order. Only those with files are offered. */
export const PLANNED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh', name: '中文' },
  { code: 'fr', name: 'Français' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'ko', name: '한국어' },
  // Offered since 2026-10-08: the right-to-left layout was looked at and
  // Guillaume said "you can unhide Arabic, it was fine". Still hidden on the
  // phone, which has no right-to-left layout yet (mobile-app/src/i18n).
  { code: 'ar', name: 'العربية', rtl: true },
] as const;

/*
 * English is bundled (it is the fallback and the source); every other language
 * is its own small file fetched only when chosen. Bundling all of them put
 * ~2.5 MB of text nobody reads into the page everyone downloads (2026-09-26).
 */
type Bundle = Record<string, unknown>;
const englishFiles = import.meta.glob('./locales/en/*.json', { eager: true }) as Record<string, { default: Bundle }>;
const otherFiles = import.meta.glob(['./locales/*/*.json', '!./locales/en/*.json']) as Record<string, () => Promise<{ default: Bundle }>>;

const parse = (path: string) => path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);

const resources: Record<string, Record<string, Bundle>> = { en: {} };
for (const [path, mod] of Object.entries(englishFiles)) {
  const m = parse(path);
  if (m) resources.en[m[2]] = mod.default ?? (mod as unknown as Bundle);
}
const translated = new Set(Object.keys(otherFiles).map((p) => parse(p)?.[1]).filter(Boolean) as string[]);

/*
 * A language marked hidden (Arabic, until its right-to-left layout has been
 * looked at on real screens) can be previewed by setting localStorage
 * "copyfi.i18n.preview" to "1" in the browser console. Nobody else sees it.
 */
let preview = false;
try { preview = localStorage.getItem('copyfi.i18n.preview') === '1'; } catch { /* no storage */ }

export const AVAILABLE_LANGUAGES = PLANNED_LANGUAGES.filter(
  (l) => (l.code === 'en' || translated.has(l.code)) && (preview || !('hidden' in l && l.hidden)),
);
export const LANGUAGE_STORAGE_KEY = 'copyfi.language';

const loaded = new Set<string>(['en']);
/** Fetches a language's files once; resolves when they are in. */
export async function loadLanguage(lng: string): Promise<void> {
  const code = (lng || 'en').split('-')[0];
  if (loaded.has(code)) return;
  const paths = Object.keys(otherFiles).filter((p) => parse(p)?.[1] === code);
  await Promise.all(paths.map(async (p) => {
    const mod = await otherFiles[p]();
    i18n.addResourceBundle(code, parse(p)![2], mod.default ?? (mod as unknown as Bundle), true, true);
  }));
  loaded.add(code);
}

/** Switch language: fetch it first, so the screen never flashes keys. */
export async function switchLanguage(lng: string): Promise<void> {
  const from = i18n.language;
  await loadLanguage(lng);
  await i18n.changeLanguage(lng);
  // Planned in the tracking plan, never sent until 2026-10-22. Imported late:
  // analytics.ts must not load before i18n has.
  if (from !== lng) void import('@/lib/analytics').then((m) => m.track('language_changed', lng, undefined, { from, to: lng })).catch(() => undefined);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Until a language's files arrive, English shows; then screens re-render.
    partialBundledLanguages: true,
    react: { bindI18nStore: 'added' },
    fallbackLng: 'en',
    supportedLngs: AVAILABLE_LANGUAGES.map((l) => l.code),
    // "es-MX" in the browser → "es".
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    ns: Object.keys(resources.en ?? { common: {} }),
    defaultNS: 'common',
    // Flat keys ("sidebar.dashboard"): one line per sentence in every file,
    // so a diff reads English against Spanish line by line.
    keySeparator: false,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

/** Right-to-left and the <html lang> follow the language. */
function applyDocumentLanguage(lng: string) {
  const code = (lng || 'en').split('-')[0];
  const rtl = PLANNED_LANGUAGES.some((l) => l.code === code && 'rtl' in l && l.rtl);
  document.documentElement.lang = code;
  document.documentElement.dir = rtl ? 'rtl' : 'ltr';
}
applyDocumentLanguage(i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);
/*
 * The language detected at start (saved choice or browser) loads now, then is
 * applied again so screens and the picker switch to it.
 *
 * 2026-09-26: this read `i18n.resolvedLanguage || i18n.language`. With only
 * English in the page at start, resolvedLanguage is "en", so the saved
 * language was never fetched: after a reload a French user got English, and an
 * Arabic one got English laid out right to left ("Live) 4(", Guillaume).
 */
export const startupLanguage: Promise<void> = (async () => {
  const detected = (i18n.language || 'en').split('-')[0];
  if (detected === 'en' || !AVAILABLE_LANGUAGES.some((l) => l.code === detected)) return;
  await loadLanguage(detected);
  await i18n.changeLanguage(detected);
})();

export default i18n;
